import { createApiRef } from '@backstage/core-plugin-api';
import {
  ClaudeFlowSession,
  CreateSessionRequest,
  UpdateSessionRequest,
  SessionLogs,
  SessionTemplate,
  CreateSessionTemplateRequest,
  ContainerStats,
} from './types';

/**
 * API for interacting with Claude Flow sessions
 */
export interface ClaudeFlowApi {
  /**
   * List all sessions for the current user
   */
  listSessions(): Promise<ClaudeFlowSession[]>;

  /**
   * Get a specific session by ID
   */
  getSession(sessionId: string): Promise<ClaudeFlowSession>;

  /**
   * Create a new session
   */
  createSession(request: CreateSessionRequest): Promise<ClaudeFlowSession>;

  /**
   * Update a session
   */
  updateSession(sessionId: string, request: UpdateSessionRequest): Promise<ClaudeFlowSession>;

  /**
   * Delete a session
   */
  deleteSession(sessionId: string): Promise<void>;

  /**
   * Start a session (run the Docker container)
   */
  startSession(sessionId: string): Promise<ClaudeFlowSession>;

  /**
   * Stop a session (stop the Docker container)
   */
  stopSession(sessionId: string): Promise<ClaudeFlowSession>;

  /**
   * Get session logs
   */
  getSessionLogs(sessionId: string): Promise<SessionLogs>;

  /**
   * Get available GitHub repositories
   */
  getRepositories(): Promise<string[]>;

  /**
   * Get branches for a specific repository
   */
  getBranches(repository: string): Promise<string[]>;

  /**
   * Get available startup scripts
   */
  getStartupScripts(): Promise<{ value: string; label: string }[]>;

  /**
   * Get available FUELIX token keys from vault
   */
  getFuelixTokens(): Promise<{ tokens: string[]; hasTokenSecret: boolean }>;

  /**
   * List all session templates
   */
  listSessionTemplates(): Promise<SessionTemplate[]>;

  /**
   * Get a specific session template by ID
   */
  getSessionTemplate(templateId: string): Promise<SessionTemplate>;

  /**
   * Create a new session template
   */
  createSessionTemplate(request: CreateSessionTemplateRequest): Promise<SessionTemplate>;

  /**
   * Delete a session template
   */
  deleteSessionTemplate(templateId: string): Promise<void>;

  /**
   * Get container stats for a session
   */
  getSessionStats(sessionId: string): Promise<{
    sessionId: string;
    containerId?: string;
    stats?: ContainerStats;
    info?: {
      id: string;
      created: string;
      state: any;
      config: {
        image: string;
        env: string[];
        cmd: string[];
      };
    };
  }>;

  /**
   * Get container state for a session
   */
  getSessionState(sessionId: string): Promise<{
    sessionId: string;
    containerId?: string;
    state: string;
    sessionStatus: string;
  }>;

  /**
   * Stream container stats (returns EventSource URL)
   */
  getSessionStatsStreamUrl(sessionId: string): string;

  /**
   * Get available Backstage users
   */
  getAvailableUsers(): Promise<Array<{
    ref: string;
    name: string;
    displayName?: string;
    email?: string;
  }>>;

  /**
   * Get available Backstage groups
   */
  getAvailableGroups(): Promise<Array<{
    ref: string;
    name: string;
    displayName?: string;
    members?: string[];
  }>>;

  /**
   * Get groups for a specific user
   */
  getUserGroups(userRef: string): Promise<string[]>;

  /**
   * Generate authenticated URL for accessing a session container
   */
  getAuthenticatedContainerUrl(sessionId: string): Promise<string>;

  /**
   * Get Claude Flow configuration
   */
  getConfig(): Promise<{
    backstageUrl: string;
    requireAuth: boolean;
  }>;
}

/**
 * API reference for the Claude Flow API
 */
export const claudeFlowApiRef = createApiRef<ClaudeFlowApi>({
  id: 'plugin.claude-flow.api',
});
